/**
 * Parse portal contact identity from comment content.
 *
 * Vault comments embed the portal contact as a prefix:
 *   {{contact:12x1234:Mario Rossi}}actual comment text
 *
 * This utility extracts the author info and strips the prefix
 * from the visible content.
 */

export interface CommentAuthor {
  /** Resolved display name */
  name: string;
  /** Contact WSID (e.g. "12x1234"), empty for CRM users */
  contactId: string;
}

const CONTACT_PREFIX_RE = /^\{\{contact:([^:}]+):([^}]*)\}\}/;

/**
 * Parse a comment's content and author fields to resolve the actual author.
 *
 * Priority:
 * 1. Embedded {{contact:id:name}} prefix in commentcontent
 * 2. creator_name field (CRM-set, often empty for portal)
 * 3. assigned_user_idename.reference (resolved user name)
 * 4. Fallback to empty string
 */
export function resolveCommentAuthor(comment: {
  commentcontent?: string;
  creator_name?: string;
  assigned_user_idename?: { reference?: string };
}): CommentAuthor {
  const content = comment.commentcontent || "";
  const match = content.match(CONTACT_PREFIX_RE);

  if (match) {
    return {
      name: match[2] || match[1],
      contactId: match[1],
    };
  }

  return {
    name:
      comment.creator_name ||
      comment.assigned_user_idename?.reference ||
      "",
    contactId: "",
  };
}

/**
 * Strip the {{contact:...}} prefix from comment content for display.
 */
export function stripContactPrefix(content: string): string {
  return content.replace(CONTACT_PREFIX_RE, "");
}
